import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Thermometer, ArrowsClockwise, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { api } from "../lib/api";

const AUTO_REFRESH_MS = 60 * 1000; // 1 min in-app refresh; server-side refresh runs every 5 min during market hours

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      hour: "2-digit", minute: "2-digit", hour12: true,
      day: "2-digit", month: "short",
    });
  } catch {
    return iso;
  }
};

/** Pick a tile background based on the sector index % change. */
function tileTone(pct) {
  if (pct == null) return { bg: "hsl(var(--surface))", border: "hsl(var(--subtle))", text: "hsl(var(--muted-fg))" };
  if (pct >= 1.5)  return { bg: "hsl(var(--signal-positive) / 0.9)", border: "hsl(var(--signal-positive))", text: "hsl(var(--bg))" };
  if (pct >= 0.5)  return { bg: "hsl(var(--signal-positive) / 0.55)", border: "hsl(var(--signal-positive) / 0.7)", text: "hsl(var(--bg))" };
  if (pct >= 0.1)  return { bg: "hsl(var(--signal-positive) / 0.22)", border: "hsl(var(--signal-positive) / 0.35)", text: "hsl(var(--signal-positive))" };
  if (pct > -0.1)  return { bg: "hsl(var(--surface))", border: "hsl(var(--subtle))", text: "hsl(var(--secondary-fg))" };
  if (pct > -0.5)  return { bg: "hsl(var(--signal-negative) / 0.22)", border: "hsl(var(--signal-negative) / 0.35)", text: "hsl(var(--signal-negative))" };
  if (pct > -1.5)  return { bg: "hsl(var(--signal-negative) / 0.55)", border: "hsl(var(--signal-negative) / 0.7)", text: "hsl(var(--bg))" };
  return { bg: "hsl(var(--signal-negative) / 0.9)", border: "hsl(var(--signal-negative))", text: "hsl(var(--bg))" };
}

function isMarketHoursIST() {
  // Mon-Fri 09:15-15:30 IST
  const now = new Date();
  const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const day = ist.getDay();
  if (day === 0 || day === 6) return false;
  const m = ist.getHours() * 60 + ist.getMinutes();
  return m >= 9 * 60 + 15 && m <= 15 * 60 + 30;
}

export default function SectorHeatmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const tickRef = useRef(null);

  const load = useCallback(async (force = false) => {
    setErr("");
    if (force) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = force
        ? await api.post("/sectors/heatmap/refresh")
        : await api.get("/sectors/heatmap");
      setData(data);
    } catch (e) {
      console.error("/sectors/heatmap", e);
      setErr(e?.response?.data?.detail || "Failed to load sector heatmap");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  // Auto-refresh every 60s during market hours so the heatmap stays live.
  useEffect(() => {
    const tick = () => {
      if (isMarketHoursIST()) load(false);
    };
    tickRef.current = setInterval(tick, AUTO_REFRESH_MS);
    return () => clearInterval(tickRef.current);
  }, [load]);

  const sectors = data?.sectors || [];

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-8 border border-subtle bg-surface/40 rounded-sm p-5"
      data-testid="sector-heatmap"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Thermometer size={14} weight="bold" className="text-pos" />
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-primary-fg">
              Live NSE sector heatmap
            </div>
            {isMarketHoursIST() && (
              <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-pos">
                <span className="w-1.5 h-1.5 rounded-full bg-pos animate-pulse" />
                live
              </span>
            )}
          </div>
          <p className="text-xs text-muted-fg leading-relaxed max-w-2xl">
            Color = sector index % today · counts = constituents bullish (+0.25%
            or more), bearish (-0.25% or worse), neutral · auto-refresh every
            5 min during market hours.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            Updated {fmtIST(data?.updated_at)}
          </span>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="heatmap-refresh-btn"
            className="px-3 py-1.5 border border-subtle rounded-sm text-secondary-fg hover:bg-surface-hover text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
          >
            <ArrowsClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="p-3 mb-3 border border-neg-soft bg-neg-soft text-neg text-sm rounded-sm">
          {err}
        </div>
      )}

      {loading && !data ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {Array.from({ length: 15 }).map((_, i) => (
            <div key={i} className="h-28 border border-subtle bg-app rounded-sm animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="sector-heatmap-grid">
          {sectors.map((s) => {
            const tone = tileTone(s.index_pct);
            const pct = typeof s.index_pct === "number" ? s.index_pct : null;
            return (
              <div
                key={s.symbol}
                data-testid={`heatmap-tile-${s.symbol}`}
                className="rounded-sm border p-3 flex flex-col gap-2 transition hover:scale-[1.02]"
                style={{ backgroundColor: tone.bg, borderColor: tone.border, color: tone.text }}
                title={`${s.name} · ${pct != null ? (pct >= 0 ? "+" : "") + pct + "%" : "—"}`}
              >
                <div className="flex items-start justify-between gap-1">
                  <span className="text-[10px] font-mono uppercase tracking-widest opacity-90 truncate" style={{ color: tone.text }}>
                    {s.name.replace("NIFTY ", "")}
                  </span>
                  {pct != null && (
                    <span className="font-mono tabular-nums text-sm font-semibold">
                      {pct >= 0 ? "+" : ""}
                      {pct.toFixed(2)}%
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[10px] font-mono tabular-nums">
                  <span className="flex items-center gap-0.5">
                    <ArrowUp size={9} weight="bold" />
                    <span className="font-semibold">{s.bullish}</span>
                  </span>
                  <span className="flex items-center gap-0.5">
                    <ArrowDown size={9} weight="bold" />
                    <span className="font-semibold">{s.bearish}</span>
                  </span>
                  <span className="opacity-80">· {s.neutral} neut</span>
                  <span className="ml-auto opacity-80">{s.bullish_pct}% bull</span>
                </div>
                {s.top_gainer && (
                  <div className="text-[10px] font-mono opacity-90 truncate" title={s.top_gainer.name}>
                    ↑ {s.top_gainer.symbol.replace(".NS","")} {s.top_gainer.change_pct >= 0 ? "+" : ""}{s.top_gainer.change_pct.toFixed(2)}%
                  </div>
                )}
                {s.top_loser && s.top_loser.symbol !== s.top_gainer?.symbol && (
                  <div className="text-[10px] font-mono opacity-90 truncate" title={s.top_loser.name}>
                    ↓ {s.top_loser.symbol.replace(".NS","")} {s.top_loser.change_pct >= 0 ? "+" : ""}{s.top_loser.change_pct.toFixed(2)}%
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </motion.section>
  );
}
